import {IAMApplication} from "../app-manager/am-model";
import {Spec} from "swagger-schema-official";
import {
    IDSApplication,
    IDSApplicationConfigDetails,
    IDSApplicationEndpoint,
    IDSApplicationResources,
    IDSAppSwaggerChecksum,
    IDSDeploymentStatus
} from "../domain-server/ds-model";

export interface IAOApplicationStatus {
    action: string;
    appId: string;
    code: string;
    details: string;
    lastError: string;
    logs: null | string[];
    message: string;
    read: boolean;
    status: string;
}

export interface IAOApplicationOwner {
    email: string;
    firstName: string;
    lastName: string;
    role: string;
    status: string;
    timestamp: Date;
    userEntityId: string;
}

export interface IAOTSCUsers {
    numberOfRecords: number;
    users: IAOApplicationOwner[];
}

export interface IAOTSCOrg {
    orgId: string;
    subscriptionId: string;
    displayName: string;
    isCurrentOrg: boolean;
}

export interface IAOTSCOrgs {
    orgs: IAOTSCOrg[];
    numberOfRecords?: number;
}

export interface IAOBuildTypeMetadata {
    systemProperties: { name: string, type: string, description: string, value: string, label: string }[];
}

export interface IAOProperty {
    name: string;
    description: string;
    datatype: string;
    default: string;
}

export interface IAOPropOverride {
    name: string;
    value: string;
    system: boolean; // indicate if the override is system or not
}

export const enum AOStatusStage {
    COMPLETED = "completed",
    SCALING = "scaling",
    BUILDING = "building",
    WAITING = "waiting",
    PROMOTING = "promoting",
    UPGRADING = "upgrading",
    ERROR = "error",
    VALIDATING = "validating",
    PUSHING = "pushing",
    SUCCESS = "success"
}
export const enum AOActionStage {
    UNKNOWN = "unknown",
    CONFIGURE = "configure",
    COMPLETED = "completed",
    SCALE = "scale",
    PUSH = "push"
}

export interface IAOAppStatus extends IAMApplication {
    action: AOActionStage;
    status: AOStatusStage;
    message: string;
    details: string;
    lastError?: string;
    maxCount?: number;
    autoScale?: boolean;
    restartsCount?: number;
}

export interface IAOStatusResponse {
    appResponses: IAOAppStatus[];
    statusCode: number;
}

export interface IAOAppCreateResponse {
    appId: string;
    status: string;
    message: string;
    success?: boolean;
}

export interface IAOErrorResponse {
    errorCode: string;
    errorMsg: string;
    errorDetail?: string;
}

/**
 * interface IAOApplicationEndpoint
 */
export interface IAOApplicationEndpoint extends IDSApplicationEndpoint {
    id: string;
    endpointName: string;
    basePath: string;
    port: number;
    protocol: string;
    pingable: boolean;
    type: string;
    ping: string;
    hasSwagger: boolean;
    name: string;
    title: string;
    description: string;
    /**
     * The URL of this endpoint
     */
    url?: string;
    /**
     * The Swagger Spec of this endpoint
     */
    swaggerJson?: Spec;
    /**
     * The URL to retrieve the Swagger Spec
     */
    swaggerUrl?: string;
}

// tslint:disable-next-line:no-empty-interface
export interface IAODeploymentStatus extends IDSDeploymentStatus {
}

/**
 * interface IAOAppSwaggerChecksum
 */
export interface IAOAppSwaggerChecksum extends IDSAppSwaggerChecksum {
    [checksumId: string]: string;
}

/**
 * interface IAOApplicationConfigDetails
 */
export interface IAOApplicationConfigDetails extends IDSApplicationConfigDetails {
    /**
     * application property prefix
     */
    propertyPrefix: string;
    /**
     * application property
     */
    properties: { name: string, datatype: string, default: string }[];

    /**
     * User system properties
     */
    userSysProperties: { name: string, datatype: string, default: string, description: string }[];

    /**
     * application property overrides
     */
    propertyOverrides: { name: string, value: string }[];
    /**
     * Native system property overrides
     */
    sysPropertyOverrides: { name: string, value: string }[];
}


/**
 * interface IAOApplicationResources
 */
export interface IAOApplicationResources extends IDSApplicationResources {
    /**
     * physical memory
     */
    physicalMemory: number;
    /**
     * total memory
     */
    totalMemory: number;
    /**
     * CPU quota
     */
    cpuQuota: number;
}

export interface IAOAppAutoScaleConfig {
    /**
     * autoScale
     */
    autoScaleEnabled: boolean;
    lowLoadFactor: number;
    highLoadFactor: number;
}


/**
 * IAOAssetsTagsInfo
 */
export interface IAOAssetTagsInfo {
    id: string;
    tagName: string;
    userName: string;
    subscriptionId: string;
    agentIds?: string[];
    appIds?: string[];
    connectionIds?: string[];
}

// tslint:disable-next-line:no-empty-interface
export interface IAOApplicationTags extends IAOAssetTagsInfo {
}

// tslint:disable-next-line:no-empty-interface
export interface IAOConnectionTags extends IAOAssetTagsInfo {
}

/**
 * Asset Tag mapping for the gsbc + uid + tagId
 */
export interface IAOOrgAssetInfo {
    /**
     * application tags
     */
    applications?: IAOApplicationTags;
    /**
     * connection tags
     */
    connections?: IAOConnectionTags;
}

/**
 * IAOAssetTagList
 */
export interface IAOAssetTagList {
    tags: IAOAssetTagsInfo[];
}


/**
 *  interface IAOApplication
 */
export interface IAOApplication extends IDSApplication {
    /**
     * application name
     */
    applicationName?: string;
    /**
     * description
     */
    description?: string;
    /**
     * application type
     */
    appType?: string;
    /**
     * organization id
     */
    orgId?: string;
    /**
     * user name
     */
    userName?: string;
    /**
     * application version
     */
    version?: string;
    /**
     * last updated time
     */
    lastUpdatedTime?: number;
    /**
     * created time
     */
    createdTime?: number;
    /**
     * desired instance count
     */
    desiredInstanceCount?: number;
    /**
     * last modified by whom
     */
    lastModifiedBy?: string;
    /**
     * application type
     */
    applicationType?: string;
    /**
     * fully qualified docker image URL
     */
    fullyQualifiedDockerImageUrl?: string;
    /**
     * endpoint ids
     */
    endpointIds?: string[];
    /**
     * Full information of all application endpoints
     */
    endpoints?: IAOApplicationEndpoint[];
    /**
     * volumes from
     * TODO: elaborate data structure
     */
    volumesFrom?: any;
    /**
     * allocated application resources
     */
    resources?: IAOApplicationResources;
    /**
     *
     */
    configDetails?: IAOApplicationConfigDetails;
    /**
     * VPN connection id
     */
    vpnConnectionId?: string;
    /**
     * Swagger checksum
     * TODO: elaborate data structure
     */
    swaggerChecksum?: IAOAppSwaggerChecksum;
    /**
     * deployment stage
     */
    deploymentStage: IAODeploymentStatus;
    /**
     * Tib Tunnel access key
     */
    tibTunnelAccessKey?: string;
    /**
     * application id
     */
    id: string;
    /**
     * whether this is a sample application
     */
    isSampleApp?: boolean;
    /**
     * Sandbox id
     */
    sandboxId?: string;
    /**
     * Application owner's name
     */
    ownerName?: string;
    /**
     * Application owner's username
     */
    owner?: string;
    /**
     * Subscription ID
     */
    subscriptionId?: string;
    /**
     *
     */
    localAcctId?: string;
    /**
     * Endpoint visibility
     */
    endpointVisibility?: string;
    /**
     * Is packaged or not
     */
    isPackaged?: boolean;
    /**
     * The entity ID of the creator
     */
    createdBy?: string;
    /**
     * The ID of the application from which this one was copied
     * TODO: need to verify the type and meaning
     */
    copyFrom?: string;
    /**
     * The ID of the application from which this one was replaced
     * TODO: need to verify the type and meaning
     */
    replaceFrom?: string;
    /**
     * The entity ID of the current user
     */
    currentUser?: string;
    /**
     * The name of the creator
     */
    createdByName?: string;
    /**
     * The sandbox name
     */
    sandboxName?: string;
    /**
     * External non-ping endpoints num
     */
    openEndpointsCount?: number;
    /**
     * Auto Scale settings
     */
    autoScaleConfig?: IAOAppAutoScaleConfig;
    /**
     * App initial status returned server
     */
    appStatus?: string;
    /**
     * metrics enabled flag for legacy apps
     */
    metricsEnabled?: boolean;
    /**
     * Last run
     */
    lastRun?: number;
    /*
     * Is onpremise app or not
     */
    onpremise?: boolean;
    /**
     * app tags
     */
    tagNames?: string[];
    /**
     * internal app tags as an array of stringified json objects
     */
    tags?: string[];
    /**
     * app tags added
     */
    tagsAdded?: string[];
    /**
     * app tags removed
     */
    tagsRemoved?: string[];
    /**
     * Is Remote Management Enabled
     */
    remoteManagement?: boolean;
}

export interface IAOSupplement {
  category: string;
  supplementedFor: string;
  supplementFileName: string;
  date: number;
}

export interface IAOSupplementResponse {
  bw: {
    supplements?: IAOSupplement[]
  };
  flogo: {
    supplements?: IAOSupplement[]
  };
}

export interface IAOSupplementUploadResponse {
  appId: string;
  status: string;
  code: string;
  message: string;
  details: string;
}

export enum SUPPLEMENT_APP_ID {
  FLOGO = "flogoconnector",
  BW = "bwplugin"
}

/**
 * CIC2 - Direct Access endpoint (pod instance info)
 */
export interface IAOApplicationPodInfo {
    podIP: string;
    podID: string;
}

export interface IAOApplicationInstanceInfo {
    /**
     * CIC2 prop - Application id
     */
    appId?: string;
    /**
     * CIC2 prop - Subscription id
     */
    subscriptionId?: string;
    /**
     * CIC2 prop - Namespace
     */
    namespace?: string;
    /**
     * CIC2 prop - Application type
     */
    appType?: string;
    /**
     * CIC2 prop - Pod info (ip and id)
     */
    pods?: IAOApplicationPodInfo[];
    /**
     * CIC1 prop - Container id's
     */
    instances?: string[];
}

export interface IAOAlertRule {
    appIds: string[];
    ruleId: string;
    subscriptionId: string;
    createdTime: string;
    modifiedTime: string;
    createdby: string;
    modifiedBy: string;
}

export interface IAOSupportedAlertRule {
    ruleId: string;
    ruleGroup: string;
    description: string;
}

export interface IAOAlertManagerLabels {
    alertname: string;
    app_id: string;
    app_type: string;
    appname: string;
    instance_id: string;
    orgname: string;
    region: string;
    severity: string;
}

export interface IAOAlertManager {
    status: string;
    labels: IAOAlertManagerLabels;
    annotations: {
        description: string
    };
    startsAt: string;
    endsAt: string;
    generatorURL: string;
    fingerprint: string;
}

export interface IAOAlertHook {
    id: string;
    name: string;
    URL: string;
    status: string;
}

export interface IAOAlertHookStatus {
    message: string;
}

export interface IAOAlertNotification {
    subscriptionId: string;
    appId: string;
    CreatedTime: string;
    Alert: IAOAlertManager;
}
